import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { Box, Chip, LinearProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";
import take from "lodash/take";
import { useMemo, useState } from "react";
import { useAuthContext } from "../auth/AuthContext";
import InfiniteScroll from "../components/infiniteScroll/InfiniteScroll";
import dayjs from "../dayjs";
import type { ECommerceOfferProductWithNames, ECommerceOfferWithNames } from "../eCommerce/eCommerceTypes";
import { useFilteredECommerceOffers } from "../eCommerce/useFilteredECommerceOffers";
import { useLimit } from "../misc/useLimit";
import { useOpenInNewTab } from "../misc/useOpenInNewTab";
import { LightboxDialog } from "../publicPackages2/LightboxDialog";
import { ECommerceGridCard } from "./ECommerceGridCard";
import { ECommerceListCard } from "./ECommerceListCard";
import { ECommerceRouteDialog } from "./ECommerceRouteDialog";
import { ECommerceFilter } from "./filter/ECommerceFilter";
import { useECommerceSearch } from "./useECommerceSearch";

export type ECommerceOffer = Pick<
  ECommerceOfferWithNames,
  "id" | "name" | "info" | "files" | "image" | "tags" | "days" | "start" | "participantsNumber" | "packageId" | "map"
> & {
  readonly products: ECommerceProduct[];
};

export type ECommerceProduct = Pick<
  ECommerceOfferProductWithNames,
  "name" | "date" | "providerName" | "options" | "description" | "contact" | "id" | "price" | "currency"
>;

type OfferGroup = {
  key: string;
  weekNumber: number;
  year: number;
  from: Dayjs;
  to: Dayjs;
  offers: ECommerceOffer[];
};

function groupOffersByWeek(offers: ECommerceOffer[]): OfferGroup[] {
  return Object.values(
    offers.reduce<Record<string, OfferGroup>>((acc, offer) => {
      const start = offer.start;
      if (!start) return acc;

      const date = dayjs(start);
      const key = `${date.isoWeekYear()}-${date.isoWeek()}`;

      acc[key] ??= {
        key,
        weekNumber: date.isoWeek(),
        year: date.isoWeekYear(),
        from: date.startOf("isoWeek"),
        to: date.endOf("isoWeek"),
        offers: [],
      };

      if (!acc[key].offers.some((o) => o.packageId === offer.packageId)) {
        acc[key].offers.push(offer);
      }

      return acc;
    }, {}),
  );
}

const ECommerceOffers = () => {
  const { context } = useAuthContext();
  const { searchCriteria } = useECommerceSearch();
  const { offers, isLoading } = useFilteredECommerceOffers({ ...searchCriteria, packageOnly: true });
  const { openInNewTab } = useOpenInNewTab();
  const [viewStyle, setViewStyle] = useState("list");
  const { limit, loadMore } = useLimit();

  const [selectedRoute, setSelectedRoute] = useState<ECommerceProduct[] | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string>();
  const offersByWeek = useMemo(() => groupOffersByWeek(offers), [offers]);

  const role = context?.role;

  if (isLoading) {
    return <LinearProgress data-testid="loading-indicator" />;
  }
  return (
    <Box sx={{ animation: "fadeIn 0.4s ease-out", pb: 8 }}>
      {" "}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
          }}
        >
          Sklep E-commerce B2B
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
          }}
        >
          Przeglądaj gotowe wycieczki udostępnione do sprzedaży. Znajdź idealny wyjazd dla swojej grupy i sprawdź wolne
          terminy.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
          gap: 4,
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            position: { xs: "relative", md: "sticky" },
            top: { xs: 0, md: 90 },
            zIndex: 10,
            alignSelf: "start",
            height: "100%",
          }}
        >
          <ECommerceFilter />
        </Box>
        <Box>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
              }}
            >
              Dostępne wycieczki:{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                {offers.length} pakiety
              </Box>
            </Typography>
            <ToggleButtonGroup
              value={viewStyle}
              exclusive
              onChange={(e, newView) => {
                if (newView) setViewStyle(newView);
              }}
              size="small"
              aria-label="wybór widoku"
              sx={{ bgcolor: "background.paper" }}
            >
              <ToggleButton value="grid" aria-label="widok siatki" sx={{ px: 2 }}>
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label="widok listy" sx={{ px: 2 }}>
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <InfiniteScroll
            dataLength={limit < offersByWeek.length ? limit : offersByWeek.length}
            hasMore={limit < offersByWeek.length}
            fetchData={loadMore}
          >
            <Stack spacing={4}>
              {take(offersByWeek, limit).map((week) => (
                <Box key={week.key}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                      mb: 2,
                      pb: 1.5,
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Tydzień {week.weekNumber}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      od {week.from.format("DD.MM.YYYY")} do {week.to.format("DD.MM.YYYY")}
                    </Typography>
                    <Chip
                      label={`${week.offers.length}`}
                      size="small"
                      color="primary"
                      sx={{ ml: "auto !important", fontWeight: "bold" }}
                    />
                  </Stack>

                  <Box>
                    {viewStyle === "grid" ? (
                      <Box
                        sx={{
                          display: "grid",
                          // Elastyczna siatka: karty mają min 320px szerokości i dopasowują się do ekranu
                          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                          gap: 3,
                        }}
                      >
                        {week.offers.map((offer) => (
                          <ECommerceGridCard
                            key={offer.id.value}
                            value={offer}
                            selectRoute={setSelectedRoute}
                            onContextMenu={() => openInNewTab(`/orders/${offer.id.value}`)}
                            onMapClick={setLightboxImage}
                            role={role}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Stack spacing={3}>
                        {week.offers.map((offer) => (
                          <ECommerceListCard
                            key={offer.id.value}
                            value={offer}
                            selectRoute={setSelectedRoute}
                            onContextMenu={() => openInNewTab(`/orders/${offer.id.value}`)}
                            onMapClick={setLightboxImage}
                            role={role}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </InfiniteScroll>
        </Box>
      </Box>
      <LightboxDialog imageUrl={lightboxImage} onClose={() => setLightboxImage(undefined)} />
      <ECommerceRouteDialog selectedRoute={selectedRoute} handleCloseRoute={() => setSelectedRoute(null)} />
    </Box>
  );
};

export default ECommerceOffers;
